

























































































































/* eslint-disable no-underscore-dangle */
import { defineComponent, watch, watchEffect, ref, computed } from '@vue/composition-api';

import store from '@/store';
import { DatasetGenerationRequest, VideoList, UserLevel } from '@/types';
import { actions } from '@/controller';
import { generateDatasetMenu } from '../state';
import { useClient, useVideos } from '@/use';
import { sleep } from '@/utils';

const defaultNumberOfSamples = 500;
const headers = [
  { text: 'Video Filename', value: 'name' },
  { text: 'Subject Type', value: 'subject_type', sortable: false },
  { text: 'Data Source', value: 'data_source', sortable: false },
  { text: 'Total Frames', value: 'number_of_frames' },
];

export default defineComponent({
  name: 'GenerateDatasetMenu',
  setup() {
    /**
     * Basic Refs
     */
    const numberOfSamples = ref(defaultNumberOfSamples);
    const client = useClient();
    const {
      items,
      selected,
      loading,
      fetch,
      videoTableInfo,
      videoTableOptions,
      itemsPerPage,
      search,
      filterSubjectType,
      filterDataSource,
    } = useVideos();

    /**
     * Request Params
     */
    const sampleTextFieldError = computed(() => (
      numberOfSamples.value <= 0
      || (
        selected.value[0]
        && numberOfSamples.value > selected.value[0].number_of_frames
      )
    ));
    const availableStudyTypes = computed(() => store.state.application.applicationData?.lists.study_types);
    const selectedStudyType = ref<string | null>(null);
    const setDefaultStudyType = () => { selectedStudyType.value = availableStudyTypes.value?.[0] || null; };

    const subjectTypes = computed(() => store.state.application.applicationData?.lists.subject_types.map((st) => st.toUpperCase()));
    const dataSources = computed(() => {
      // If user level is standard user, just return their allowed institutions
      if (client?.user?.user_level === UserLevel.User || client?.user?.user_level === UserLevel.Userhigh) {
        return client.user?.accessible_institutions;
      }

      // Otherwise return all possible
      return store.state.application.applicationData?.lists.institutions;
    });

    // Populate default once the list of study types exists
    watchEffect(setDefaultStudyType);

    const currentDatetimeStr = ref<string | null>(null);
    const setCurrentDatetime = () => { currentDatetimeStr.value = new Date().toISOString(); };

    setCurrentDatetime();
    const currentDatetimeInterval = setInterval(setCurrentDatetime, 1000);

    const datasetName = ref<string | null>(null);
    const datasetNameRules = [
      (val: string | null) => (!!val && val.length > 0),
    ];

    const datasetId = computed(() => `${datasetName.value}::${currentDatetimeStr.value}`);

    /**
     * State Checking
     */
    const generateDatasetError = ref<string | null>(null);
    const numberOfSamplesDisabled = computed(() => (
      selectedStudyType.value === 'motion'
    ));
    const generateDisabled = computed(() => (
      (selected.value.length !== 1 && !numberOfSamplesDisabled)
      || sampleTextFieldError.value
      || availableStudyTypes.value === undefined
      || selectedStudyType.value === null
    ));

    /**
     * Functions/handlers
     */

    function closeDialog() {
      generateDatasetMenu.value = false;
    }

    function handleClick(video: VideoList) {
      selected.value = [video];
    }

    async function generateRemoteDataset() {
      const selectedVideo = selected.value[0];
      const currentUser = client?.user;
      const study_type = selectedStudyType.value;
      if (
        !selected
        || !datasetName.value
        || !currentUser
        || !study_type
        || sampleTextFieldError.value
      ) {
        return;
      }

      loading.value = true;

      const video = selectedVideo.name;
      const dataset_id = datasetId.value;

      const number_of_samples = numberOfSamples.value;
      const request: DatasetGenerationRequest = {
        video,
        params: { dataset_id, study_type, number_of_samples },
      };

      const resp = await client.generateRemoteDataset(request);
      if (resp.status_code !== 200) {
        // TODO: Display this error in the UI
        generateDatasetError.value = resp.message as string;
      } else {
        // Close Menu and open newly created dataset
        await sleep(1000);
        actions.fetchDataset(dataset_id, false);
        clearInterval(currentDatetimeInterval);
        closeDialog();
      }

      loading.value = false;
    }

    /**
     * Watcher Declarations
     */

    watch(generateDatasetMenu, (open) => {
      if (open) fetch();
    });

    const selectedVideoMaxFrames = computed(() => {
      if (!selected.value.length) {
        return defaultNumberOfSamples;
      }

      return Math.min(selected.value[0].number_of_frames, defaultNumberOfSamples);
    });
    function changeNumberOfSamples(val: number) {
      const clamped = Math.min(Math.max(val, 0), selectedVideoMaxFrames.value);
      numberOfSamples.value = clamped;
    }

    // Update the datasetId if the selection changes
    watch(selected, () => {
      const video = selected.value[0]?.name;
      const currentUser = client?.user;
      if (!video || !currentUser) {
        return;
      }

      // Set dataset name
      const { username } = currentUser;
      datasetName.value = `${username}::${video}`;

      // Set default number of samples
      numberOfSamples.value = selectedVideoMaxFrames.value;
    });

    return {
      headers,
      subjectTypes,
      dataSources,
      items,
      search,
      filterSubjectType,
      filterDataSource,
      numberOfSamples,
      changeNumberOfSamples,
      selectedVideoMaxFrames,
      availableStudyTypes,
      selectedStudyType,
      currentDatetimeStr,
      datasetName,
      datasetNameRules,
      datasetId,
      loading,
      selected,
      sampleTextFieldError,
      generateDisabled,
      numberOfSamplesDisabled,
      handleClick,
      closeDialog,
      generateDatasetError,
      generateRemoteDataset,
      videoTableOptions,
      videoTableInfo,
      itemsPerPage,
    };
  },
});
